/**
 * mk-decor — parallax for the scattered decor objects.
 *
 * This is a PORT, not a new feature. The static build's js/decor.js selector
 * `.mk-decor-item[data-parallax]` genuinely matched its markup (18 items) and
 * the drift was verified running before porting — unlike the hero's confetti,
 * whose selector matched nothing and had never run.
 *
 * One thing changes in Horizon, and it matters: the old code was driven by
 * `window.ScrollEngine.register`, and that engine only fired when
 * `window.scrollY` changed. Above 990px `.page-wrapper` scrolls instead of the
 * document, so scrollY is permanently 0 and the callback would never have run.
 * The maths here is viewport-relative (getBoundingClientRect), so it was always
 * correct — it simply was never called. Driven by mk-scroll-engine it now works
 * on both sides of the breakpoint.
 *
 * Component rules that keep this alive across a theme-editor morph:
 *   - never cache child nodes; read this.refs inside the frame callback
 *   - always unsubscribe in disconnectedCallback (double-attach is invisible
 *     until the drift runs at double speed)
 *   - gate with the engine's observe(), never a hand-rolled IntersectionObserver
 *   - updatedCallback does NOT fire (measured at task 4) — nothing may depend
 *     on it
 */

import { Component } from '@theme/component';
import { subscribe, observe } from '@theme/mk-scroll-engine';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** Depth 1..4 maps to 10px..40px of travel, clamped. */
const MIN_TRAVEL = 10;
const MAX_TRAVEL = 40;

class MkDecor extends Component {
  /** @type {(() => void) | null} */
  #unsubscribe = null;
  /** @type {(() => void) | null} */
  #unobserve = null;

  connectedCallback() {
    super.connectedCallback();

    // rootMargin mirrors the static build's 100px buffer, so items are already
    // in position by the time the section edge reaches the viewport.
    this.#unobserve = observe(
      this,
      (isVisible) => {
        if (isVisible) this.#start();
        else this.#stop();
      },
      { rootMargin: '100px 0px 100px 0px' }
    );
  }

  updatedCallback() {
    // Measured at task 4: this never fires. Kept because it is the documented
    // hook and may fire for other morph callers. Nothing here depends on it —
    // #render reads this.refs fresh every frame.
    super.updatedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#unobserve?.();
    this.#unobserve = null;
  }

  #reduced() {
    return window.matchMedia(REDUCED_MOTION).matches;
  }

  /** @returns {HTMLElement[]} */
  #items() {
    const found = this.refs?.item;
    if (!found) return [];
    return Array.isArray(found) ? found : [found];
  }

  #start() {
    if (this.#unsubscribe) return;
    if (this.#reduced()) return;
    if (!this.#items().length) return;

    for (const el of this.#items()) el.classList.add('mk-decor-item--animating');
    this.#unsubscribe = subscribe(() => this.#render());
  }

  #stop() {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    for (const el of this.#items()) el.classList.remove('mk-decor-item--animating');
  }

  /**
   * Progress of the host section through the viewport, -1 (entering from the
   * bottom) to 1 (leaving past the top). Deliberately reads geometry rather
   * than the scroll offset, so it is correct whichever container is scrolling.
   */
  #render() {
    const host = this.parentElement ?? this;
    const rect = host.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    const entryPoint = viewportHeight;
    const exitPoint = -rect.height;
    const totalTravel = entryPoint - exitPoint;
    if (totalTravel <= 0) return;

    let progress = ((entryPoint - rect.top) / totalTravel) * 2 - 1;
    progress = Math.max(-1, Math.min(1, progress));

    for (const el of this.#items()) {
      const depth = parseFloat(el.dataset.parallax) || 1;
      const travel = Math.max(MIN_TRAVEL, Math.min(MAX_TRAVEL, depth * 10));
      // transform only. The items carry their angle on the separate `rotate`
      // property, which this does not disturb — writing `transform` here would
      // otherwise wipe the rotation.
      el.style.transform = `translate3d(0, ${progress * travel}px, 0)`;
    }
  }
}

if (!customElements.get('mk-decor')) {
  customElements.define('mk-decor', MkDecor);
}
